import { TrendingUp, TrendingDown, Award } from "lucide-react";
import type { Template } from "../api/endpoints/fetchTemplates";

interface TemplateAnalyticsPanelProps {
  template: Template;
}

export function TemplateAnalyticsPanel({ template }: TemplateAnalyticsPanelProps) {
  const title = template.fileTitle || template.fileName || "Untitled Template";

  const summary = {
    responses: 42,
    trend: 7,
    avgResponseTimeMin: 9.8,
    completionRate: 91,
  };

  const completionData = [
    { month: "Jul", value: 82 },
    { month: "Aug", value: 79 },
    { month: "Sep", value: 85 },
    { month: "Oct", value: 89 },
    { month: "Nov", value: 68 },
    { month: "Dec", value: 75 },
  ];

  const insights = [
    { text: `${title} completion rate is ${summary.completionRate}%`, type: "success" as const },
    { text: `Avg response time ~ ${summary.avgResponseTimeMin}m`, type: "info" as const },
    { text: `Fields number increased ${summary.trend}% vs last period`, type: "success" as const },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-normal text-gray-900">Analytics: {title}</h3>
        <div className={`flex items-center gap-1 text-xs ${summary.trend > 0 ? "text-green-600" : "text-red-600"}`}>
          {summary.trend > 0 ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          <span>{Math.abs(summary.trend)}%</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900">General Statistics</h4>
          <span className="text-xs text-gray-500">Overview</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl p-4 border border-gray-200 bg-gradient-to-br from-gray-50 to-white">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <Award className="w-3 h-3 text-blue-600" />
              </div>
              <p className="text-xs text-gray-500">Fields</p>
            </div>
            <p className="text-xl font-medium text-gray-900 tracking-tight">{summary.responses}</p>
          </div>
          <div className="rounded-xl p-4 border border-gray-200 bg-gradient-to-br from-green-50 to-white">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-3 h-3 text-green-600" />
              </div>
              <p className="text-xs text-gray-500">Completion Rate</p>
            </div>
            <p className="text-xl font-medium text-gray-900 tracking-tight">{summary.completionRate}%</p>
          </div>
          <div className="rounded-xl p-4 border border-gray-200 bg-gradient-to-br from-indigo-50 to-white">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                <TrendingDown className="w-3 h-3 text-indigo-600" />
              </div>
              <p className="text-xs text-gray-500">Avg. Draft Time</p>
            </div>
            <p className="text-xl font-medium text-gray-900 tracking-tight">{summary.avgResponseTimeMin}m</p>
          </div>
          <div className="rounded-xl p-4 border border-gray-200 bg-gradient-to-br from-amber-50 to-white">
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${template.published ? "bg-emerald-100" : "bg-amber-100"}`}>
                <span className={`w-2 h-2 rounded-full ${template.published ? "bg-emerald-600" : "bg-amber-600"}`} />
              </div>
              <p className="text-xs text-gray-500">Status</p>
            </div>
            <p className="text-sm font-medium text-gray-900 inline-flex items-center gap-2">
              {template.published ? "Published" : "Draft"}
              <span className={`px-2 py-0.5 text-xs rounded-full border ${template.published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{template.published ? "Live" : "In Review"}</span>
            </p>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-normal text-gray-900">Completion Trend</h3>
          <span className="text-xs text-gray-500">Last 6 months</span>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-end justify-between h-32 gap-2">
            {completionData.map((item, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-primary rounded-t hover:bg-primary-hover transition-all cursor-pointer"
                  style={{ height: `${item.value}px` }}
                  title={`${item.value}% completion`}
                />
                <span className="text-xs text-gray-500">{item.month}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-center gap-2 text-xs">
          <TrendingUp className="w-4 h-4 text-green-600" />
          <span className="text-gray-700">
            <span className="text-green-600 font-medium">+{summary.trend}%</span> completion rate vs last period
          </span>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-normal text-gray-900 mb-4">Key Insights</h3>
        <div className="space-y-2">
          {insights.map((insight, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-3 p-3 rounded-lg border ${insight.type === "success"
                ? "bg-green-50 border-green-200"
                : insight.type === "warning"
                  ? "bg-yellow-50 border-yellow-200"
                  : "bg-blue-50 border-blue-200"
                }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${insight.type === "success"
                  ? "bg-green-500"
                  : insight.type === "warning"
                    ? "bg-yellow-500"
                    : "bg-blue-500"
                  }`}
              />
              <p className="text-xs text-gray-700 flex-1">{insight.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <button className="w-full text-sm text-gray-700 hover:text-gray-900 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
          Export Report for {title}
        </button>
      </div>
    </div>
  );
}

export default TemplateAnalyticsPanel;
